<template>
  <b-sidebar
    id="add-new-receipt-document-description-sidebar"
    :visible="isAddNewReceiptDocumentDescriptionSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @hidden="resetForm"
    @change="(val) => $emit('update:is-add-new-receipt-document-description-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          {{ button_text }}收據單據備註
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <b-overlay
          :show="loading"
          spinner-variant="primary"
          spinner-type="grow"
          spinner-small
          rounded="sm"
        >
          <!-- Form -->
          <b-form
            class="p-2"
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm"
          >
            <!-- 名稱 -->
            <validation-provider
              #default="validationContext"
              name="此欄"
              rules="required"
              vid="v_name"
            >
              <b-form-group label="名稱" label-for="name">
                <b-form-input
                  id="name"
                  v-model="receiptDocumentDescriptionData.name"
                  autofocus
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="名稱"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- 描述 -->
            <validation-provider
              #default="validationContext"
              name="此欄"
              rules="required"
              vid="v_description"
            >
              <b-form-group label="描述" label-for="description">
                <b-form-textarea
                  id="description"
                  v-model="receiptDocumentDescriptionData.description"
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="描述"
                  rows="6"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- 排序 -->
            <validation-provider
              #default="validationContext"
              name="此欄"
              rules="required"
              vid="v_sort_order"
            >
              <b-form-group label="排序" label-for="sort_order">
                <b-form-input
                  id="sort_order"
                  v-model="receiptDocumentDescriptionData.sort_order"
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="排序"
                  type="text"
                />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- 狀態 -->
            <b-form-group label="狀態" label-for="is_active">
              <b-form-checkbox
                id="is_active"
                v-model="receiptDocumentDescriptionData.is_active"
                name="is_active"
                :value="1"
                :unchecked-value="0"
              >
                啟用
              </b-form-checkbox>
            </b-form-group>

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-2"
                type="submit"
              >
                {{ button_text }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-secondary"
                @click="hide"
              >
                取消
              </b-button>
            </div>
          </b-form>
        </b-overlay>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BOverlay,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { ref, watch } from '@vue/composition-api'
import { required } from '@validations'
import formValidation from '@core/comp-functions/forms/form-validation'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BOverlay,
    vSelect,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isAddNewReceiptDocumentDescriptionSidebarActive',
    event: 'update:is-add-new-receipt-document-description-sidebar-active',
  },
  props: {
    isAddNewReceiptDocumentDescriptionSidebarActive: {
      type: Boolean,
      required: true,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    id: {
      type: Number,
      required: true,
    },
  },
  setup(props, { emit }) {
    const loading = ref(false)

    const blankReceiptDocumentDescriptionData = {
      id: 0,
      name: '',
      description: '',
      sort_order: 1,
      is_active: 1,
    }
    const button_text = ref('新增')

    const receiptDocumentDescriptionData = ref(JSON.parse(JSON.stringify(blankReceiptDocumentDescriptionData)))

    const resetReceiptDocumentDescriptionData = () => {
      receiptDocumentDescriptionData.value = JSON.parse(JSON.stringify(blankReceiptDocumentDescriptionData))
    }

    const toast = useToast()

    watch(
      () => [props.id, props.isAddNewReceiptDocumentDescriptionSidebarActive],
      () => {
        if (props.id > 0 && props.isAddNewReceiptDocumentDescriptionSidebarActive) {
          receiptDocumentDescriptionData.value = { ...props.data }

          if (receiptDocumentDescriptionData.value.id > 0) {
            button_text.value = '修改'
          } else {
            button_text.value = '新增'
          }
        } else {
          button_text.value = '新增'
        }
      }
    )

    const onSubmit = () => {
      loading.value = true
      store
        .dispatch('receipt-document-description/addReceiptDocumentDescription', receiptDocumentDescriptionData.value)
        .then(() => {
          emit('refetch-data')
          emit('update:is-add-new-receipt-document-description-sidebar-active', false)
          toast({
            component: ToastificationContent,
            props: {
              title: `成功${button_text.value}收據單據備註`,
              icon: 'EditIcon',
              variant: 'success',
            },
          })
        })
        .catch(error => {
          console.log(error)
          toast({
            component: ToastificationContent,
            props: {
              title: `${button_text.value}收據單據備註時出錯`,
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          loading.value = false
        })
    }

    const {
      refFormObserver,
      getValidationState,
      resetForm,
    } = formValidation(resetReceiptDocumentDescriptionData)

    return {
      receiptDocumentDescriptionData,
      onSubmit,
      refFormObserver,
      getValidationState,
      resetForm,
      button_text,
      loading,
      required,
    }
  },
}
</script>
